import i18n from 'I18n';

import { ACTION_BUTTON_STATUS } from 'component/campaign/CheckInSection';
import {
  CAMPAIGN_TYPE_COUPON,
  CAMPAIGN_TYPE_EARNING,
  CAMPAIGN_TYPE_STAMP,
  CAMPAIGN_TYPE_TICKET,
  CampaignAPIHelper,
  EARNING_RULE_BIRTH,
  EARNING_RULE_GPS,
  EARNING_RULE_LEVEL_UPGRADE,
  EARNING_RULE_NEW_MEMBER,
  EARNING_RULE_PURCHASE,
  EARNING_RULE_QR_CODE,
  EARNING_RULE_REFER,
} from 'services/CampaignAPIHelper';

import {
  createAction,
  delay,
  enLocaleNumberFormatter,
  generateTakeLeading,
  getTranslationData,
} from 'utils';
import {
  checkIfStarted,
  errorMessageIfFailedToGetRewards,
} from 'utils/CampaignUtil';
import { HIDE_REWARD_CLAIM_SECTION, NO_LEVEL } from 'utils/Config';
import {
  ADDITIONAL_DATA_TYPE,
  CustomerFieldName,
  DefaultCustomerFieldConfig,
  EarningRuleGlobalLimitInfoType,
  EarningRuleRewardAmountLogicType,
  EarningRuleRewardLogicType,
  GenderType,
  TicketCampaignQuestionType,
} from 'utils/CustomEnums';
import {
  convertDateToStamp,
  formatDate,
  getCampaignDetailEndDate,
  getCurrentDateFormatted,
  getMyAccountUserBirthdayForUpdate,
} from 'utils/DateTimeUtils';
import { checkCouponIsUnavaiable } from './BuyCoupon';

const takeLeading = generateTakeLeading('campaignDetail');

const getDefaultInstructionTitle = (campaignType) => {
  if (campaignType === CAMPAIGN_TYPE_COUPON) {
    return i18n.t('campaignDetail:howToUse');
  }
  if ([CAMPAIGN_TYPE_STAMP, CAMPAIGN_TYPE_EARNING].includes(campaignType)) {
    return i18n.t('campaignDetail:howToEarn');
  }
  return null;
};

const checkIsCoupon = (campaignType) => campaignType === CAMPAIGN_TYPE_COUPON;
const checkIsTicket = (campaignType) => campaignType === CAMPAIGN_TYPE_TICKET;

const parseCouponCampaignData = (apiData) => {
  const requiredPoint = apiData.coupon_campaign_type_required_points || 0;
  const point = enLocaleNumberFormatter(requiredPoint);
  let status =
    requiredPoint > 0 ? ACTION_BUTTON_STATUS.NORMAL : ACTION_BUTTON_STATUS.GET;
  if (!checkIfStarted(apiData.start_date)) {
    status = ACTION_BUTTON_STATUS.UNAVAILABLE;
  }
  return {
    campaignType: CAMPAIGN_TYPE_COUPON,
    earningRuleType: null,
    textTitle: i18n.t('campaignDetail:pointsRequired'),
    point,
    status,
    startDate: convertDateToStamp(apiData.start_date),
    endDate: convertDateToStamp(apiData.end_date),
  };
};

const parseTicketCampaignData = (apiData) => {
  const requiredPoint = 0;
  let status = ACTION_BUTTON_STATUS.GET_TICKET;
  if (!checkIfStarted(apiData.start_date)) {
    status = ACTION_BUTTON_STATUS.UNAVAILABLE;
  }
  return {
    campaignType: CAMPAIGN_TYPE_TICKET,
    earningRuleType: null,
    textTitle: null,
    point: null, // enLocaleNumberFormatter(requiredPoint)
    status,
    startDate: convertDateToStamp(apiData.start_date),
    endDate: convertDateToStamp(apiData.end_date),
  };
};

const parseEarning = (apiData) => {
  const earningRule = apiData.earning_campaign_type_earning_rule;
  const earningType = earningRule.type;
  let keyPrefix = null;
  if (earningType === EARNING_RULE_GPS) {
    keyPrefix = 'gps_check_in_type_';
  } else if (earningType === EARNING_RULE_QR_CODE) {
    keyPrefix = 'qr_code_scanning_type_';
  } else if (earningType === EARNING_RULE_REFER) {
    keyPrefix = 'member_referral_type_';
  } else if (earningType === EARNING_RULE_BIRTH) {
    keyPrefix = 'birthday_type_';
  } else if (earningType === EARNING_RULE_NEW_MEMBER) {
    keyPrefix = 'new_member_type_';
  } else if (earningType === EARNING_RULE_PURCHASE) {
    keyPrefix = 'general_purchase_type_';
  }

  if (keyPrefix === null) {
    return {};
  }
  let point = null;
  let textTitle = null;
  let rewardType = 'POINTS';
  if (earningRule[`${keyPrefix}reward_type`] === 'COUPON') {
    point = `X ${earningRule[`${keyPrefix}coupon_reward_type_quantity`]}`;
    textTitle = i18n.t('campaignDetail:couponsEarn');
    rewardType = 'COUPON';
  } else {
    if (earningType === EARNING_RULE_PURCHASE) {
      point =
        `${enLocaleNumberFormatter(
          earningRule[
            `general_purchase_type_points_reward_type_points_per_x_dollars_spent`
          ] || 0,
        )} ` + i18n.t('points');
    } else {
      point = `+${enLocaleNumberFormatter(
        earningRule[`${keyPrefix}points_reward_type_points`] || 0,
      )}`;
    }
    textTitle = i18n.t('campaignDetail:pointsEarn');
  }
  return {
    campaignType: CAMPAIGN_TYPE_EARNING,
    earningRuleType: earningType,
    rewardType,
    textTitle,
    point,
    status: checkIfStarted(apiData.start_date)
      ? ACTION_BUTTON_STATUS.NORMAL
      : ACTION_BUTTON_STATUS.UNAVAILABLE,
    startDate: convertDateToStamp(apiData.start_date),
    endDate: convertDateToStamp(apiData.end_date),
  };
};

const parseStamp = (apiData) => {
  const redeemedRewardCoupons = apiData?.stamp_campaign_reward_coupons ?? [];
  return {
    campaignType: apiData.type,
    earningRuleType: null,
    redeemedRewardCoupons,
    redeemedRewards: apiData.stamp_redeemed_rewards || [],
    earnedStamps: apiData.earned_stamps,
    startDate: convertDateToStamp(apiData.start_date),
    endDate: convertDateToStamp(apiData.end_date),
    stampBadge: {
      badgeSlot: apiData.stamp_campaign_type_empty_slot_stamp_image,
      badgeAcquired: apiData.stamp_campaign_type_acquired_stamp_image,
      giftSlot: apiData.stamp_campaign_type_empty_gift_slot_image,
      giftAcquire: apiData.stamp_campaign_type_collected_gift_image,
    },
  };
};

const parseActionBarData = (apiData) => {
  let campaignType = apiData.type;
  const status = ACTION_BUTTON_STATUS.NORMAL;
  if (campaignType === CAMPAIGN_TYPE_COUPON) {
    return parseCouponCampaignData(apiData);
  }
  if (campaignType === CAMPAIGN_TYPE_TICKET) {
    return parseTicketCampaignData(apiData);
  }
  if (campaignType === CAMPAIGN_TYPE_EARNING) {
    return parseEarning(apiData);
  }
  if (campaignType === CAMPAIGN_TYPE_STAMP) {
    return parseStamp(apiData);
  }
  campaignType = null;
  return {
    campaignType,
    status,
  };
};

const getBrand = (brand, currentLanguage) => {
  const brandData = {
    shown: false,
    iconUrl: null,
    name: '',
    description: null,
  };

  if (brand) {
    brandData.shown = brand.name ? true : false;
    brandData.iconUrl = brand.icon_url;
    // const baseData = getTranslationData(brand, currentLanguage);
    const baseData = brand;
    brandData.name = baseData.name;
    brandData.description = baseData.description;
  }
  return brandData;
};

const parseCampaignDetail = (
  apiData,
  currentLanguage,
  lastUpdateTime = null,
) => {
  const baseData = getTranslationData(apiData, i18n.language);
  // const baseData = apiData;
  const hasDescription = (baseData.detail_section_content || '').length > 0;
  const hasInstructions =
    (baseData.instruction_section_content || '').length > 0;

  const couponTemplate = apiData.coupon_campaign_type_coupon_template;
  const isTicket = checkIsTicket(apiData.type);
  const outOfStock = isTicket
    ? !apiData.ticket_total_stock
    : apiData.coupon_campaign_type_coupon_stock === 0;

  const data = {
    campaignID: apiData.id,
    campaignType: apiData.type,
    inBlackoutPeriod: apiData.is_within_blackout_period,
    images: baseData.photos_url || [
      'campaignDetail/slider/test1.jpg',
      'campaignDetail/slider/test2.jpeg',
      'campaignDetail/slider/test3.jpg',
    ],
    basic: {
      endDate: getCampaignDetailEndDate(apiData.end_date),
      isFeatured: apiData.is_featured,
      isOnlyForYou: apiData.is_only_for_you,
      isActive: apiData.is_active,
      outOfStock,
      name: baseData.name,
    },
    brand: getBrand(getTranslationData(apiData.brand), currentLanguage),
    // brand: apiData.brand_section,
    summary: baseData.short_description,
    description: {
      shown: hasDescription,
      title: baseData.detail_section_title || i18n.t('campaignDetail:details'),
      content: baseData.detail_section_content,
      showTopDiver: hasDescription && hasInstructions,
    },
    instructions: {
      shown: hasInstructions,
      title:
        baseData.instruction_section_title ||
        getDefaultInstructionTitle(apiData.type),
      content: baseData.instruction_section_content,
      showBottomDivder: false,
    },
    coupon: {
      isCoupon: checkIsCoupon(apiData.type),
      requiredPoints: apiData.coupon_campaign_type_required_points,
      leftStock: apiData.coupon_campaign_type_coupon_stock,
      lastUpdateTime:
        lastUpdateTime === null
          ? getCurrentDateFormatted(true, false)
          : formatDate(lastUpdateTime, null, true, false),
      // stores:
      //   (apiData.coupon_campaign_type_coupon_template || {})
      //     .applicable_stores || [],
      stores: apiData.campaign_stores?.map((store) =>
        getTranslationData(store),
      ),
      title: (apiData.coupon || {}).title,
      perLimit: apiData.coupon_campaign_type_per_head_limit,
      customerStoreSelectionBehavior: couponTemplate?.should_use_in_store
        ? couponTemplate?.customer_store_selection_behavior
        : null,
      customerOnlineOfflineSelectionBehavior:
        couponTemplate?.show_online_offline_selection
          ? couponTemplate?.customer_online_offline_selection_behavior
          : null,
    },
    ticket: {
      isTicket,
      requiredPoints: apiData.ticket_campaign_type_required_points,
      leftStock: apiData.ticket_total_stock,
      showStock: apiData.ticket_campaign_type_should_show_coupon_stock,
      lastUpdateTime:
        lastUpdateTime === null
          ? getCurrentDateFormatted(true, false)
          : formatDate(lastUpdateTime, null, true, false),
      campaignID: apiData.id,
      campaignName: baseData.name,
      limit: apiData.ticket_campaign_type_per_head_limit,
      showOtherInfo: apiData.ticket_campaign_should_fill_other_information,
      enableOtp: apiData.ticket_campaign_enable_otp,
    },
  };
  return data;
};

const parseStampTask = (data, currentLanguage, actionBar) => {
  const typeEarningRule = {
    MEMBER_REFERRAL: {
      text: i18n.t('campaignDetail:referAFriend'),
      cardType: 'button',
    },
    QR_CODE_SCANNING: {
      text: i18n.t('campaignDetail:scanQrCode'),
      cardType: 'button',
    },
    GPS_CHECK_IN: {
      text: i18n.t('campaignDetail:checkIn'),
      cardType: 'button',
    },
    GENERAL_PURCHASE: {
      text: i18n.t('records:record_upload_receipt'),
      cardType: HIDE_REWARD_CLAIM_SECTION ? '' : 'button',
    },
  };

  const parseList = data.map((aData) => {
    const translateData = getTranslationData(aData, currentLanguage);
    const { type } = translateData;
    const startDate = convertDateToStamp(actionBar.startDate);
    const isStarted = checkIfStarted(startDate);

    let rewardStampCount;
    let rewardString;
    let rewardStringSecond = '';
    let rewardOfEachString = '';
    if (aData.type === EARNING_RULE_PURCHASE) {
      if (
        aData.general_purchase_type_reward_amount_logic_type !==
        EarningRuleRewardAmountLogicType.DYNAMIC
      ) {
        rewardString = `+${aData.general_purchase_type_stamp_reward_type_stamps_per_x_value}`;
      } else if (
        aData.general_purchase_type_reward_logic_type ===
        EarningRuleRewardLogicType.ITEM_QUANTITY
      ) {
        rewardString = `+${aData.general_purchase_type_stamp_reward_type_stamps_per_x_value}`;
        rewardStringSecond = 'campaignDetail:qualifiedItem';
        rewardOfEachString = `${aData.general_purchase_type_stamp_reward_type_x}`;
      } else {
        rewardString = `+${aData.general_purchase_type_stamp_reward_type_stamps_per_x_value}`;
        rewardStringSecond = 'campaignDetail:HKD';
        rewardOfEachString = `${aData.general_purchase_type_stamp_reward_type_x}`;
      }
    } else {
      const rewardKeyPostfix = '_type_stamp_reward_type_quantity';
      const earningRuleKeyMapping = {
        [EARNING_RULE_REFER]: 'member_referral',
        [EARNING_RULE_BIRTH]: 'birthday',
        [EARNING_RULE_GPS]: 'gps_check_in',
        [EARNING_RULE_QR_CODE]: 'qr_code_scanning',
        [EARNING_RULE_NEW_MEMBER]: 'new_member',
        [EARNING_RULE_LEVEL_UPGRADE]: 'level_upgrade',
      };
      const key = earningRuleKeyMapping[aData.type] + rewardKeyPostfix;
      rewardStampCount = aData[key];
      rewardString = `+${rewardStampCount}`;
    }

    return {
      title: translateData.name,
      rewardStampCount,
      rewardString,
      rewardStringSecond,
      rewardOfEachString,
      earningRuleID: translateData.id,
      disabled: !isStarted,
      ...typeEarningRule[type],
      buttonStatus: checkIfStarted(startDate)
        ? ACTION_BUTTON_STATUS.NORMAL
        : ACTION_BUTTON_STATUS.UNAVAILABLE,
      earningRuleType: type,
      endDate: convertDateToStamp(actionBar.endDate),
      startDate,
      isStarted,
    };
  });

  return {
    apiResult: data,
    parseList,
  };
};

const parseBadge = (data) => {
  return data.sort((a, b) => a.required_stamps - b.required_stamps);
};

const getStampCampaignSuccessToast = ({
  rewardType,
  quantity,
  stampCampaign,
}) => {
  const stampCampaignName = stampCampaign?.data?.basic?.name || '';
  if (rewardType === 'COUPON') {
    if (quantity > 0) {
      return i18n.t('campaignDetail:stampEarnCouponToast', {
        stampCampaignName,
        couponQuantity: quantity,
      });
    }
    return i18n.t('campaignDetail:earnCouponLimitToast');
  }
  if (rewardType === 'POINTS') {
    if (quantity > 0) {
      return i18n.t('campaignDetail:stampEarnPointsToast', {
        stampCampaignName,
        points: quantity,
      });
    }
    return i18n.t('campaignDetail:earnPointsLimitToast');
  }
  return '';
};


const checkIsEmptyReward = (
  rewardType,
  rewardPointCount,
  rewardTpeCount,
  rewardCouponCount,
  rewardStampCount,
) => {
  if (rewardType === EarningRuleGlobalLimitInfoType.STAMPS) {
    return !rewardStampCount;
  };
  if (rewardType === EarningRuleGlobalLimitInfoType.COUPONS) {
    return !rewardCouponCount;
  };
  if (rewardType === EarningRuleGlobalLimitInfoType.POINTS) {
    return !rewardPointCount && !rewardTpeCount;
  };
  return false;
};


const getParticipateCampaignSuccessToast = ({
  rewardType,
  points,
  tpe,
  couponTemplate,
  couponQuantity,
  stampQuantity,
}) => {
  if (rewardType === EarningRuleGlobalLimitInfoType.STAMPS) {
    if (stampQuantity > 0) {
      return i18n.t('campaignDetail:successfullyEarnedStamp', {
        count: stampQuantity,
      });
    }
    return i18n.t('campaignDetail:earnStampLimitToast');
  }
  if (rewardType === EarningRuleGlobalLimitInfoType.COUPONS) {
    if (couponQuantity > 0) {
      const couponTemplateTranslationData = getTranslationData(couponTemplate);
      return i18n.t('campaignDetail:earnCouponToast', {
        couponTemplateName: couponTemplateTranslationData?.name || '',
        couponQuantity: couponQuantity || 0,
      });
    }
    return i18n.t('campaignDetail:earnCouponLimitToast');
  }
  if (rewardType === EarningRuleGlobalLimitInfoType.POINTS) {
    if (points > 0 || tpe > 0) {
      return i18n.t('campaignDetail:earnPointsToast', {
        points: points || 0,
        tpe: tpe || 0,
      });
    }
    return i18n.t('campaignDetail:earnPointsLimitToast');
  }
  return '';
};

const parseTicketCampaignAdditionalData = ({
  data,
  currentLanguage,
  userDetail,
  userCustomData,
  profileFieldsConfig,
  sectionsConfig,
}) => {
  const genderSections = [
    {
      label: i18n.t('personal_info_male'),
      value: GenderType.Male,
    },
    {
      label: i18n.t('personal_info_female'),
      value: GenderType.Female,
    },
    {
      label: i18n.t('personal_info_not_disclosed'),
      value: GenderType.NotDisclosed,
    },
  ];

  const otherInfo = data.other_informations?.map((question) => {
    const translateData = getTranslationData(question, currentLanguage);
    let title = translateData.question;
    let placeholder = translateData.question;
    let extraData = {};
    if (
      question.question_type === TicketCampaignQuestionType.customerProfileField
    ) {
      const fieldName = question.question;
      const config = profileFieldsConfig?.filter(
        (item) => item.fieldName === fieldName,
      )?.[0];
      const defaultFieldConfig = DefaultCustomerFieldConfig[fieldName];
      title = i18n.t(defaultFieldConfig?.title);
      placeholder = i18n.t(defaultFieldConfig?.placeholder);
      extraData = {
        // optional: false, // !config.mandatory
        questionFormat: defaultFieldConfig?.questionFormat,
        defaultValue: userDetail?.[defaultFieldConfig?.userFieldName],
        options: fieldName === CustomerFieldName.gender ? genderSections : [],
        precision: fieldName === CustomerFieldName.birthday ? 'month' : null,
      };
    } else if (
      question.question_type ===
      TicketCampaignQuestionType.customerAdditionalDataField
    ) {
      const fieldName = question.question;
      let fieldConfig = {};
      // eslint-disable-next-line no-unused-expressions
      sectionsConfig?.forEach((section) => {
        // eslint-disable-next-line no-unused-expressions
        section?.fields?.forEach((field) => {
          if (field?.fieldName === fieldName) {
            fieldConfig = field;
          }
        });
      });

      title = fieldConfig.displayTitle;
      placeholder = fieldConfig.displayTitle;
      extraData = {
        // optional: !fieldConfig?.mandatory,
        questionFormat: fieldConfig.fieldType,
        defaultValue: userCustomData?.[fieldName],
        options: fieldConfig.choices?.map((item) => ({
          value: item.fieldValue,
          label: item.displayLabel,
          translations: item.translations,
        })),
      };
    } else if (
      question.question_type === TicketCampaignQuestionType.additionalQuestion
    ) {
      // do nothing
    }

    return {
      id: question.id,
      type: question.question_type,
      optional: question.question_is_optional,
      fieldName: question.question,
      questionFormat: question.question_format,
      options:
        question.options?.map((option) => {
          const optionTranslateData = getTranslationData(
            option,
            currentLanguage,
          );
          return {
            value: option.option_name,
            label: optionTranslateData.option_name,
          };
        }) || [],
      title,
      placeholder,
      ...extraData,
    };
  });
  const tickets = data.tickets?.map((item) => {
    const translateData = getTranslationData(item, currentLanguage);
    return {
      id: item.coupon_template,
      title: translateData.time_slot,
      stockCount: item.stock,
    };
  });

  return {
    haveOtherInfo: data.ticket_campaign_should_fill_other_information,
    enableOtp: data.ticket_campaign_enable_otp,
    otherInfo,
    tickets,
  };
};

export default {
  namespace: 'campaignDetail',
  state: {
    // data: {},
    // apiResult: {},
    // isLoading: false,
    // actionBar: {},
    ticketCampaignModalLoading: false,
    ticketCampaignAdditionalData: {},
    preRegisterData: {},
    participateCampaignErrorMessage: "",
  },
  reducers: {
    updateState(state, { payload }) {
      console.log('@156 update state');
      return { ...state, ...payload };
    },
    updateCampaignDetail(state, { payload }) {
      const { data, campaignID } = payload;

      return {
        ...state,
        [campaignID]: {
          ...state[campaignID],
          ...(data.stores
            ? { data: { ...state[campaignID].data, ...data } }
            : data),
        },
      };
    },
    updateStampActionBarStatus(state, { payload }) {
      const { data, campaignID, earningRuleID } = payload;
      const { buttonStatus } = data;
      const { parseList } = state[campaignID].data.stampCampaigns.tasks;
      const newList = parseList.map((aData) => {
        const newData = { ...aData };
        if (newData.earningRuleID === earningRuleID) {
          newData.buttonStatus = buttonStatus;
        }
        return newData;
      });
      return {
        ...state,
        [campaignID]: {
          ...state[campaignID],
          data: {
            ...state[campaignID].data,
            stampCampaigns: {
              ...state[campaignID].data.stampCampaigns,
              tasks: {
                ...state[campaignID].data.stampCampaigns.tasks,
                parseList: newList,
              },
            },
          },
        },
      };
    },
  },
  effects: {
    *setCampaignID({ payload }, { put }) {
      const { campaignID } = payload;
      yield put(createAction('updateState')({ campaignID, isLoading: true }));
    },
    updateCampaignDetailActionBar: [
      function* ({ payload }, { call, put, select }) {
        const { campaignID } = payload;
        const details = yield select((state) => state.campaignDetail);
        if (campaignID in details) {
          const currentLanguage = yield select((state) => state.users.language);
          const response = yield call(
            CampaignAPIHelper.getCampaignDetail,
            campaignID,
          );
          if (response.status === 200) {
            const actionBarData = parseActionBarData(
              response.data,
              currentLanguage,
            );
            yield put.resolve(
              createAction('updateState')({
                [campaignID]: {
                  ...details[campaignID],
                  actionBar: actionBarData,
                },
              }),
            );
          }
        }
      },
      { type: 'takeLatest' },
    ],
    getCampaignDetail: [
      function* ({ payload }, { call, put, select }) {
        const { campaignID } = payload;

        const details = yield select((state) => state.campaignDetail);
        if (campaignID in details) {
          yield put(
            createAction('updateCampaignDetail')({
              campaignID,
              data: { isLoading: true, hasError: false },
            }),
          );
        }
        const response = yield call(
          CampaignAPIHelper.getCampaignDetail,
          campaignID,
        );

        if (response.status === 200) {
          const data = parseCampaignDetail(response.data, '');
          yield put(
            createAction('updateState')({
              [campaignID]: {
                data,
                apiResult: response.data,
                isLoading: true,
                actionBar: parseActionBarData(response.data),
              },
            }),
          );
          const { campaignType } = data;
          if (campaignType === CAMPAIGN_TYPE_STAMP) {
            yield put(
              createAction('getEarningRulesForStampCampaign')({
                campaignID,
              }),
            );
          } else {
            yield put(
              createAction('updateCampaignDetail')({
                campaignID,
                data: {
                  isLoading: false,
                },
              }),
            );
          }
        } else {
          yield put(
            createAction('updateState')({
              hasError: true,
            }),
          );
        }
      },
      { type: 'takeLatest' },
    ],
    getCampaignBlackout: [
      function* ({ payload }, { call, put, select }) {
        const { campaignID, onSuccess, onFailure } = payload;
        const response = yield call(
          CampaignAPIHelper.getCampaignBlackout,
          campaignID,
        );

        if (response.status === 200) {
          console.log('response.data', response.data);
          const inBlackoutPeriod = response.data?.in_blackout_period;
          if (inBlackoutPeriod) {
            onFailure();
          } else {
            onSuccess();
          }
        } else {
          onFailure();
        }
      },
      { type: 'takeLatest' },
    ],
    *getCampaignDetailAndGoRedeemPage({ payload }, { call }) {
      const { campaignID, callBackFunction } = payload;
      const response = yield call(
        CampaignAPIHelper.getCampaignDetail,
        campaignID,
      );
      if (response.status === 200) {
        const data = parseCampaignDetail(response.data, '');

        callBackFunction(data);
      }
    },
    verifyQrCode: [
      function* ({ payload }, { call, put, select }) {
        const {
          qrCodeString,
          campaignID,
          earningRuleID,
          inStampCampaign,
          rewardStampCount,
        } = payload;
        const customerID = yield select((state) => state.users.userInfo.uid);
        const appInstallationID = yield select(
          (state) => state.users.appInstallationID,
        );
        const startTime = Date.now();
        yield put(
          createAction('updateActionButtonStatus')({
            earningRuleID,
            campaignID,
            status: ACTION_BUTTON_STATUS.LOADING,
            inStampCampaign,
          }),
        );
        const response = yield call(
          CampaignAPIHelper.participateCampaign,
          customerID,
          {
            qr_code: qrCodeString,
            app_installation: appInstallationID, // hardcoded now
            stamp_earning_rule: earningRuleID,
            campaign: campaignID,
            customer: customerID,
          },
        );
        const endTime = Date.now();
        if (endTime - startTime < 1000) {
          yield call(delay, 1000 - endTime + startTime);
        }
        if (response.status < 300) {
          if (response?.data?.stamp_reward_coupon_out_of_stock) {
            yield put(
              createAction('participateStampCampaignCouponSoldOut')({
                earningRuleID,
                campaignID,
                inStampCampaign,
              }),
            );
          } else {
            yield put(
              createAction('participateCampaignSuccess')({
                earningRuleID,
                campaignID,
                inStampCampaign,
                rewardStampCount,
                responseData: response.data,
              }),
            );
          }
        } else {
          yield put(
            createAction('participateCampaignFailed')({
              campaignID,
              response,
              earningRuleID,
              inStampCampaign,
            }),
          );
        }
      },
      { type: 'takeLatest' },
    ],
    verifyGPS: [
      function* ({ payload }, { call, put, select }) {
        const {
          latitude,
          longitude,
          campaignID,
          earningRuleID,
          inStampCampaign,
          rewardStampCount,
        } = payload;
        const customerID = yield select((state) => state.users.userInfo.uid);
        const appInstallationID = yield select(
          (state) => state.users.appInstallationID,
        );
        const startTime = Date.now();
        yield put(
          createAction('updateActionButtonStatus')({
            earningRuleID,
            campaignID,
            status: ACTION_BUTTON_STATUS.LOADING,
            inStampCampaign,
          }),
        );
        const response = yield call(
          CampaignAPIHelper.participateCampaign,
          customerID,
          {
            check_in_latitude: latitude.toFixed(6),
            check_in_longitude: longitude.toFixed(6),
            app_installation: appInstallationID,
            stamp_earning_rule: earningRuleID,
            campaign: campaignID,
            customer: customerID,
          },
        );
        const endTime = Date.now();
        if (endTime - startTime < 1000) {
          yield call(delay, 1000 - endTime + startTime);
        }
        if (response.status < 300) {
          if (response?.data?.stamp_reward_coupon_out_of_stock) {
            yield put(
              createAction('participateStampCampaignCouponSoldOut')({
                earningRuleID,
                campaignID,
                inStampCampaign,
              }),
            );
          } else {
            yield put(
              createAction('participateCampaignSuccess')({
                earningRuleID,
                campaignID,
                inStampCampaign,
                rewardStampCount,
                responseData: response.data,
              }),
            );
          }
        } else {
          yield put(
            createAction('participateCampaignFailed')({
              campaignID,
              response,
              earningRuleID,
              inStampCampaign,
            }),
          );
        }
      },
      { type: 'takeLatest' },
    ],
    participateCampaignSuccess: [
      function* ({ payload }, { call, put, select }) {
        const {
          earningRuleID,
          campaignID,
          inStampCampaign,
          responseData = {},
        } = payload;
        const {
          reward_points: rewardPointCount,
          reward_tpe: rewardTpeCount,
          reward_coupon_quantity: rewardCouponCount,
          reward_coupon: rewardCouponTemplate,
          reward_stamps: rewardStampCount,
          reward_type: rewardType,
        } = responseData;
        if (earningRuleID) {
          yield put.resolve(
            createAction('updateCampaignDetailActionBar')({
              campaignID,
            }),
          );
          const updateStampCountPayload = {
            campaignID,
            rewardStampCount,
          };
          yield put.resolve(
            createAction('campaignList/updateStampCampaignEarnedStampCount')(
              updateStampCountPayload,
            ),
          );
          yield put.resolve(
            createAction('offerList/updateStampCampaignEarnedStampCount')(
              updateStampCountPayload,
            ),
          );
        }

        const isEmptyReward = checkIsEmptyReward(
          rewardType,
          rewardPointCount,
          rewardTpeCount,
          rewardCouponCount,
          rewardStampCount,
        );

        yield put(
          createAction('updateActionButtonStatus')({
            earningRuleID,
            campaignID,
            status: isEmptyReward ? ACTION_BUTTON_STATUS.DISABLE : ACTION_BUTTON_STATUS.TICK,
            inStampCampaign,
          }),
        );
        yield put(createAction('users/fetchBalance')());
        const toastMessage = getParticipateCampaignSuccessToast({
          rewardType,
          points: rewardPointCount,
          tpe: rewardTpeCount,
          couponQuantity: rewardCouponCount,
          couponTemplate: rewardCouponTemplate,
          stampQuantity: rewardStampCount,
        });
        yield put({
          type: 'navBars/updateState',
          payload: {
            toastShowing: {
              value: true,
              content: toastMessage,
              showTickIcon: true,
            },
          },
        });
      },
      { type: 'takeLatest' },
    ],

    participateStampCampaignCouponSoldOut: [
      function* ({ payload }, { call, put }) {
        const { campaignID, earningRuleID, inStampCampaign } = payload;
        yield put(
          createAction('updateActionButtonStatus')({
            earningRuleID,
            campaignID,
            status: ACTION_BUTTON_STATUS.NORMAL,
            inStampCampaign,
          }),
        );
        const errorMessage =
          'No luck! The reward you unlocked with your stamps is out of stock. Keep collecting to unlock more exciting rewards next time.';
        yield put({
          type: 'navBars/updateState',
          payload: {
            toastShowing: {
              value: true,
              content: errorMessage,
            },
          },
        });
      },
      { type: 'takeLatest' },
    ],

    participateCampaignFailed: [
      function* ({ payload }, { call, put }) {
        const { campaignID, response, error, earningRuleID, inStampCampaign } =
          payload;
        let { errorMessage, showToast } = errorMessageIfFailedToGetRewards(response, error);
          yield put(
          createAction('updateActionButtonStatus')({
            earningRuleID,
            campaignID,
            status: ACTION_BUTTON_STATUS.NORMAL,
            inStampCampaign,
          }),
        );
        if (showToast) {
          yield put({
            type: 'navBars/updateState',
            payload: {
              toastShowing: {
                value: true,
                content: errorMessage,
              },
            },
          });
        } else {
          yield put({
            type: 'campaignDetail/updateState',
            payload: {
              participateCampaignErrorMessage: errorMessage
            },
          });
        }
      },
      { type: 'takeLatest' },
    ],

    updateActionButtonStatus: [
      function* ({ payload }, { call, put, select }) {
        const { earningRuleID, campaignID, status, inStampCampaign } = payload;
        if (inStampCampaign) {
          yield put(
            createAction('updateStampActionBarStatus')({
              campaignID,
              earningRuleID,
              data: { buttonStatus: status },
            }),
          );
        } else {
          yield put(
            createAction('updateState')({
              actionButtonStatus: status,
            }),
          );
        }
      },
      { type: 'takeLatest' },
    ],

    getEarningRulesForStampCampaign: [
      function* ({ payload }, { call, put, select }) {
        const { campaignID } = payload;
        const { data, actionBar } = yield select(
          (state) => state.campaignDetail[campaignID],
        );
        const currentLanguage = yield select((state) => state.users.language);
        const response = yield call(
          CampaignAPIHelper.getEarningRulesForStamp,
          campaignID,
        );
        if (response.status < 300) {
          const stampCampaigns = {
            tasks: parseStampTask(response.data, currentLanguage, actionBar),
          };
          const newData = { ...data, stampCampaigns };
          yield put.resolve(
            createAction('updateCampaignDetail')({
              campaignID,
              data: {
                data: newData,
              },
            }),
          );
          yield put(
            createAction('getRewardsForStampCampaign')({
              campaignID,
            }),
          );
        } else {
          yield put(
            createAction('updateCampaignDetail')({
              campaignID,
              data: {
                isLoading: false,
              },
            }),
          );
        }
      },
      { type: 'takeLatest' },
    ],

    getRewardsForStampCampaign: [
      function* ({ payload }, { call, put, select }) {
        const { campaignID } = payload;
        const { data } = yield select(
          (state) => state.campaignDetail[campaignID],
        );
        const response = yield call(
          CampaignAPIHelper.getRewardsForStampCampaign,
          campaignID,
        );
        if (response.status < 300) {
          const { stampCampaigns } = data;
          const badges = parseBadge(response.data);
          const newStampCampaign = { ...stampCampaigns, badges };
          const newData = { ...data, stampCampaigns: newStampCampaign };
          yield put(
            createAction('updateCampaignDetail')({
              campaignID,
              data: {
                data: newData,
                isLoading: false,
              },
            }),
          );
        } else {
          yield put(
            createAction('updateCampaignDetail')({
              campaignID,
              data: {
                isLoading: false,
              },
            }),
          );
        }
      },
      { type: 'takeLatest' },
    ],
    // Deprecated
    // watcher: takeLeading(
    //   'customerAcquireStampReward',
    //   function* ({ payload }, { call, put, select }) {
    //     const {
    //       campaignID,
    //       storeID,
    //       rewardID,
    //       stampRewardData,
    //       stampRewardCouponQuantity,
    //     } = payload;
    //     const startTime = Date.now();
    //     yield put(
    //       createAction('updateState')({
    //         actionButtonStatus: ACTION_BUTTON_STATUS.LOADING,
    //       }),
    //     );
    //     try {
    //       const response = yield call(
    //         CampaignAPIHelper.customerAcquireStampReward,
    //         campaignID,
    //         {
    //           store_id: storeID,
    //           reward_id: rewardID,
    //           app_installation: null,
    //         },
    //       );
    //       const endTime = Date.now();
    //       if (endTime - startTime < 1000) {
    //         yield call(delay, 1000 - endTime + startTime);
    //       }
    //       if (response.status < 300) {
    //         // duplicate acquire will raise 409 error
    //         // to update badge icon status status
    //         yield put(
    //           createAction('campaignDetail/updateCampaignDetailActionBar')({
    //             campaignID,
    //           }),
    //         );
    //         yield call(delay, 1000);
    //         const campaignDetail = yield select((state) => {
    //           const campaignDetails = state.campaignDetail;
    //           if (campaignID in campaignDetails) {
    //             return campaignDetails[campaignID];
    //           }
    //           return null;
    //         });
    //         // console.log("@@stamp campaign detail:", campaignDetail)
    //         const toastMessage = getStampCampaignSuccessToast({
    //           rewardType: stampRewardData.reward_type,
    //           quantity: stampRewardData.quantity || 0,
    //           stampCampaign: campaignDetail,
    //         });

    //         yield put({
    //           type: 'navBars/updateState',
    //           payload: {
    //             toastShowing: {
    //               value: true,
    //               content: toastMessage,
    //             },
    //           },
    //         });

    //         yield put(
    //           createAction('updateState')({
    //             actionButtonStatus: ACTION_BUTTON_STATUS.MYCOUPON,
    //           }),
    //         );
    //       } else {
    //         yield put(
    //           createAction('campaignDetail/updateCampaignDetailActionBar')({
    //             campaignID,
    //           }),
    //         );
    //         yield put(
    //           createAction('acquireRewardFailed')({
    //             response,
    //           }),
    //         );
    //       }
    //     } catch (error) {
    //       const endTime = Date.now();
    //       if (endTime - startTime < 1000) {
    //         yield call(delay, 1000 - endTime + startTime);
    //       }
    //       yield put(
    //         createAction('acquireRewardFailed')({
    //           error,
    //         }),
    //       );
    //     }
    //     // Wait 1 more seconds for animation/ navigation
    //     yield call(delay, 1000);
    //   },
    // ),

    // acquireRewardFailed: [
    //   function* ({ payload }, { put }) {
    //     const { response, error } = payload;
    //     let { errorMessage, showToast } = errorMessageIfFailedToGetRewards(response, error);
    //     yield put(
    //       createAction('updateState')({
    //         actionButtonStatus: ACTION_BUTTON_STATUS.NORMAL,
    //       }),
    //     );
    //     if (showToast) {
    //       yield put({
    //         type: 'navBars/updateState',
    //         payload: {
    //           toastShowing: {
    //             value: true,
    //             content: errorMessage,
    //           },
    //         },
    //       });
    //     }
    //   },
    // ],

    getTicketCampaignAdditionalData: [
      function* ({ payload }, { call, put, select }) {
        const { campaignID } = payload;

        yield put({
          type: 'updateState',
          payload: {
            ticketCampaignModalLoading: true,
            ticketCampaignAdditionalData: {},
          },
        });

        yield put.resolve({ type: 'config/getProfileConfig' });
        yield put.resolve({ type: 'config/getAdditionalDataSection' });

        const response = yield call(
          CampaignAPIHelper.getTicketCampaignAdditionalData,
          campaignID,
        );
        const {
          currentLanguage,
          userDetail,
          userCustomData,
          profileFieldsConfig,
          sectionsConfig,
        } = yield select((state) => ({
          currentLanguage: state.users.language,
          userDetail: state.users.userInfo,
          userCustomData: state.users.userInfo.additionalData,
          profileFieldsConfig: state.config.profileFieldsConfig,
          sectionsConfig: state.config.additionalDataSectionConfig,
        }));

        if (response.status < 300) {
          yield put.resolve({
            type: 'updateState',
            payload: {
              ticketCampaignAdditionalData: parseTicketCampaignAdditionalData({
                data: response.data,
                currentLanguage,
                userDetail,
                userCustomData,
                profileFieldsConfig,
                sectionsConfig,
              }),
            },
          });
        }
        yield put({
          type: 'updateState',
          payload: { ticketCampaignModalLoading: false },
        });
      },
      { type: 'takeLatest' },
    ],

    participateTicketCampaign: [
      function* ({ payload }, { call, put, select }) {
        const eventRegistrationData = yield select(
          (state) => state.users.eventRegistrationData,
        );

        const ticketOtherInfo = {};
        // eslint-disable-next-line no-unused-expressions
        payload?.questionData?.forEach((item) => {
          ticketOtherInfo[item.id] = item.defaultValue;
          let value = item.defaultValue;
          if (
            item.questionFormat === ADDITIONAL_DATA_TYPE.DATETIME &&
            !!value
          ) {
            if (
              item.type === TicketCampaignQuestionType.customerProfileField &&
              item.fieldName === CustomerFieldName.birthday
            ) {
              const preDate = new Date(value);
              value = preDate.setDate(1);
            }
            value = getMyAccountUserBirthdayForUpdate(value);
          }

          ticketOtherInfo[item.id] = value;
        });

        const participateData = {
          verify_credential: eventRegistrationData?.verifyCredential || null,
          email: eventRegistrationData?.email || null,
          mobile:
            eventRegistrationData?.countryCode &&
            eventRegistrationData?.phoneNumber
              ? `+${eventRegistrationData?.countryCode}${eventRegistrationData?.phoneNumber}`
              : null,
          campaign: payload.campaignID,
          desired_ticket: payload.selectedTimeSlotId,
          desired_ticket_quantity: payload.count,
          ticket_other_information: ticketOtherInfo,
        };

        const response = yield call(
          CampaignAPIHelper.participateTicketCampaign,
          participateData,
        );
        if (response.status < 300) {
          const userInfo = response.data?.reward_customer_detail;
          yield put({
            type: 'users/updateState',
            payload: {
              eventRegistrationData: {},
              eventRegistrationUserInfo: {
                membershipId: userInfo?.membership_id,
                ssoUid: userInfo?.sso_uid,
              },
            },
          });
          yield put(
            createAction('buyCoupon/updateState')({
              confirmButtonStatus: 2,
              couponIsUnavaiable: checkCouponIsUnavaiable(response?.data?.reward_ticket),
            }),
          );
        } else {
          yield put({
            type: 'buyCoupon/acquireRewardFailed',
            payload: {
              response,
              isTicket: true,
            },
          });
        }
        if (payload?.afterAction) {
          payload.afterAction();
        }
      },
      { type: 'takeLatest' },
    ],

    getRegisterInfo: [
      function* ({ payload }, { call, put, select }) {
        const { membershipId, ssoUid } = payload;

        yield put({
          type: 'updateState',
          payload: {
            preRegisterData: {},
          },
        });

        const inputData = {
          sso_uid: ssoUid,
          membership_id: membershipId,
        };

        const response = yield call(
          CampaignAPIHelper.getRegisterInfo,
          inputData,
        );
        if (response.status < 300) {
          const data = response.data;
          yield put({
            type: 'updateState',
            payload: {
              preRegisterData: {
                email: data?.email,
                countryCode: data?.country_code,
                phoneNumber: data?.phone_number,
              },
            },
          });
        }
      },
      { type: 'takeLatest' },
    ],
  },
};
